/* =========================================================================
   LAYER'D TOOLKIT — PWA install prompt behaviour
   File: assets/toolkit-pwa.js · loaded by snippets/toolkit-install-prompt.liquid
   (which is rendered by sections/learn-subnav.liquid on every Toolkit page).

   What it does
     · Android/Chrome: captures the browser's beforeinstallprompt event and
       offers it behind the branded banner's Install button. A real install
       prompt only — if the browser never offers one, no banner.
     · iOS Safari: no install API exists, so the banner's button opens a
       short "Share → Add to Home Screen" instruction sheet instead.
     · Never shows when already running standalone, after an install, or
       within 14 days of a dismissal (localStorage).

   GA4 events (routed through lhTrack from learn-hub-analytics.js when
   present, else gtag directly; fails silently like the rest of the suite):
     toolkit_pwa_prompt_shown / toolkit_pwa_prompt_dismissed /
     toolkit_pwa_installed — each with { platform, user_tier }.
   ========================================================================= */
(function () {
  'use strict';

  var DISMISS_KEY = 'layerd_pwa_prompt_dismissed'; // timestamp (ms) of "Not now"
  var INSTALL_KEY = 'layerd_pwa_installed';        // '1' once installed
  var REPROMPT_DAYS = 14;

  /* ---- storage that survives private-mode Safari ----------------------- */
  function lsGet(k) { try { return window.localStorage.getItem(k); } catch (e) { return null; } }
  function lsSet(k, v) { try { window.localStorage.setItem(k, v); } catch (e) { /* fine */ } }

  /* ---- analytics ------------------------------------------------------- */
  function tier() {
    var host = document.querySelector('[data-lh-tier]');
    var t = host && host.getAttribute('data-lh-tier');
    return (t === 'free' || t === 'premium' || t === 'visitor') ? t : 'visitor';
  }
  function track(eventName) {
    try {
      var params = { platform: isIOS ? 'ios' : (isMacDesktop ? 'mac' : 'android'), user_tier: tier() };
      if (typeof window.lhTrack === 'function') window.lhTrack(eventName, params);
      else if (typeof window.gtag === 'function') window.gtag('event', eventName, params);
    } catch (e) { /* analytics must never break a tool */ }
  }

  /* ---- environment ----------------------------------------------------- */
  var isStandalone =
    (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
    window.navigator.standalone === true;

  // iPhone/iPod, plus iPadOS which reports as MacIntel with a touchscreen
  var isIOS = /iP(hone|od|ad)/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  /* r47 (Mariya): "too much friction". Detect the environment precisely so
     the guide says only what is true for THIS browser:
       · in-app browsers (Instagram/Facebook/TikTok/…) have NO
         Add-to-Home-Screen — the only honest step is "open in Safari"
       · Chrome/Firefox/Edge on iOS keep Share in the TOP corner, so the
         pointer flips up and the "where" line changes
       · Safari iPhone: Share is bottom-centre — the default. */
  var ua = navigator.userAgent;
  var isInApp = /Instagram|FBAN|FBAV|FB_IAB|musical_ly|TikTok|BytedanceWebview|Snapchat|Pinterest|Line\//i.test(ua);
  var iosShareOnTop = isIOS && /CriOS|FxiOS|EdgiOS/.test(ua);
  /* iPad Safari also keeps Share top-right */
  var isIPad = /iPad/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  if (isIPad) iosShareOnTop = true;

  /* 2026-09-16 (Mariya: "I could not find it on my macbook"). A Mac desktop is
     not iOS and Safari fires no beforeinstallprompt, so nothing offered an
     install path. Detect it (Mac platform, no touch = not an iPad) and show a
     Mac instruction sheet. Chrome/Edge on Mac still get the real prompt below;
     this covers Safari and any browser that never offered one. */
  var isMacDesktop = !isIOS &&
    (navigator.platform === 'MacIntel' || /Macintosh/.test(ua)) &&
    (navigator.maxTouchPoints || 0) <= 1;

  if (isStandalone) {
    // They're inside the installed app — remember that for browser visits too
    lsSet(INSTALL_KEY, '1');
    return;
  }

  /* Dashboard "Get the app" (2026-08-16): the dismiss/install latches now
     gate only the AUTO-shown banner. Capture + wiring always run, so the
     dashboard's permanent button (window.LdPwa.open) works during the
     14-day quiet period too — that button is the user asking, which is
     exactly what the quiet period exists to protect. */
  function autoOk() {
    if (lsGet(INSTALL_KEY) === '1') return false;
    var dismissedAt = parseInt(lsGet(DISMISS_KEY) || '0', 10);
    return !(dismissedAt && (Date.now() - dismissedAt) < REPROMPT_DAYS * 864e5);
  }

  /* ---- banner wiring ---------------------------------------------------- */
  var deferredPrompt = null;
  var shownTracked = false;

  function el() { return document.getElementById('ld-pwa'); }

  /* r47: execCommand copy for webviews that refuse the clipboard API */
  function legacyCopy(text) {
    try {
      var ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      var ok = document.execCommand('copy');
      ta.remove();
      return !!ok;
    } catch (e) { return false; }
  }

  function show() {
    var banner = el();
    if (!banner || !banner.hidden) return;
    if (lsGet(INSTALL_KEY) === '1') return; // installed mid-pageview — stay quiet
    banner.hidden = false;
    if (!shownTracked) { shownTracked = true; track('toolkit_pwa_prompt_shown'); }
  }
  function hide() {
    var banner = el();
    if (banner) banner.hidden = true;
    setIosSheet(false);   /* r47: the pointer never outlives the card */
  }
  function dismiss() {
    lsSet(DISMISS_KEY, String(Date.now()));
    track('toolkit_pwa_prompt_dismissed');
    hide();
  }

  /* r47: the arrow + sheet pair. One function owns which sheet is visible
     and whether the pointer shows, so the two can never disagree. */
  function setIosSheet(open) {
    var banner = el();
    if (!banner) return;
    var ios = banner.querySelector('[data-pwa-ios-sheet]');
    var inapp = banner.querySelector('[data-pwa-inapp-sheet]');
    var arrow = document.getElementById('ld-pwa-arrow');
    if (ios) ios.hidden = !open || isInApp;
    if (inapp) inapp.hidden = !open || !isInApp;
    /* the pointer only exists where there is a true Share button to point
       at — an in-app browser gets none */
    if (arrow) {
      arrow.hidden = !open || isInApp;
      arrow.classList.toggle('is-top', iosShareOnTop);
    }
    /* Safari's Share sits below the card — lift the card off the bottom
       edge so the arrow owns it (top-corner browsers don't need the lift) */
    banner.classList.toggle('ld-pwa--lifted', open && !isInApp && !iosShareOnTop);
    if (open) {
      var where = banner.querySelector('[data-pwa-where]');
      if (where) where.textContent = iosShareOnTop ? '— top corner of the screen' : '— below, in the middle';
    }
  }

  /* the Mac sheet: instructions only, no API to call. */
  function setMacSheet(open) {
    var banner = el();
    if (!banner) return;
    var mac = banner.querySelector('[data-pwa-mac-sheet]');
    if (mac) mac.hidden = !open;
  }

  function onInstallClick() {
    if (isIOS) {
      // No install API on iOS — reveal the two-beat guide (or the
      // open-in-Safari step in an in-app browser) and the pointer
      var sheetEl = document.querySelector(isInApp ? '[data-pwa-inapp-sheet]' : '[data-pwa-ios-sheet]');
      setIosSheet(!!(sheetEl && sheetEl.hidden));
      return;
    }
    // Mac desktop with no real prompt (Safari, or Chrome/Edge that didn't offer
    // one this pageview): show the Mac steps instead of nothing.
    if (isMacDesktop && !deferredPrompt) {
      var mac = document.querySelector('[data-pwa-mac-sheet]');
      setMacSheet(!!(mac && mac.hidden));
      return;
    }
    if (!deferredPrompt) return;
    var p = deferredPrompt;
    deferredPrompt = null;
    p.prompt();
    p.userChoice.then(function (choice) {
      // appinstalled handles the happy path; declining the native sheet
      // counts as a dismissal so we respect the 14-day quiet period
      if (!choice || choice.outcome !== 'accepted') dismiss();
    }).catch(function () { /* native sheet failed — leave the banner up */ });
  }

  function wire() {
    var banner = el();
    if (!banner) return;
    var installBtn = banner.querySelector('[data-pwa-install]');
    var dismissBtn = banner.querySelector('[data-pwa-dismiss]');
    if (installBtn) {
      if (isIOS) installBtn.textContent = isInApp ? 'Open in Safari' : 'How to install';
      else if (isMacDesktop) installBtn.textContent = 'How to install';
      installBtn.addEventListener('click', onInstallClick);
    }
    if (dismissBtn) dismissBtn.addEventListener('click', dismiss);

    /* r47: the in-app browsers' way out — copy the link for Safari. The
       async clipboard API needs a secure context and can still refuse
       inside webviews, so the old execCommand path stays as the fallback. */
    var copyBtn = banner.querySelector('[data-pwa-copylink]');
    if (copyBtn) copyBtn.addEventListener('click', function () {
      var url = location.href;
      function done(ok) { copyBtn.textContent = ok ? 'Copied ✓ — now paste it in Safari' : 'Couldn’t copy — hold the address bar instead'; }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(function () { done(true); }, function () { done(legacyCopy(url)); });
      } else done(legacyCopy(url));
    });

    /* the permanent doors: subnav "Get the app" tab + the mobile corner
       button (both data-ld-installtab, markup in learn-subnav.liquid).
       Standalone never reaches here (early return above). */
    refreshDoors();
    var doors = document.querySelectorAll('[data-ld-installtab]');
    for (var i = 0; i < doors.length; i++) {
      doors[i].addEventListener('click', function (e) {
        e.preventDefault();
        openManual();
      });
    }

    // iOS and Mac have no install event to wait for (Safari fires none) — the
    // banner is the instruction, so show it up front instead of waiting.
    if ((isIOS || isMacDesktop) && autoOk()) show();
  }

  function refreshDoors() {
    var show = lsGet(INSTALL_KEY) !== '1';
    var doors = document.querySelectorAll('[data-ld-installtab]');
    for (var i = 0; i < doors.length; i++) doors[i].hidden = !show;
  }

  /* ---- manual door (the dashboard's permanent button) ------------------- */
  function openManual() {
    if (lsGet(INSTALL_KEY) === '1') return;
    var banner = el();
    if (!banner) return;
    banner.hidden = false;              // deliberate ask — latches don't apply
    track('toolkit_pwa_manual_open');
    if (isIOS) {
      /* r47: she tapped "Get the app" — open the guide already matched to
         this browser, pointer and all. One tap, one screen. */
      setIosSheet(true);
      return;
    }
    if (deferredPrompt) { onInstallClick(); return; }
    if (isMacDesktop) { setMacSheet(true); return; }
    // Chrome never offered an install this pageview — show the menu steps
    var a = banner.querySelector('[data-pwa-android-sheet]');
    if (a) a.hidden = false;
  }
  window.LdPwa = {
    isIOS: isIOS,
    installed: function () { return lsGet(INSTALL_KEY) === '1'; },
    open: openManual
  };

  /* ---- browser events --------------------------------------------------- */
  window.addEventListener('beforeinstallprompt', function (e) {
    e.preventDefault();          // stop Chrome's mini-infobar; we offer our own
    deferredPrompt = e;
    if (document.getElementById('ld-pwa') && autoOk()) show();
  });

  window.addEventListener('appinstalled', function () {
    lsSet(INSTALL_KEY, '1');
    track('toolkit_pwa_installed');
    hide();
    refreshDoors();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', wire);
  } else {
    wire();
  }
})();
